import {
  DataIntegrationService,
  Event,
  EventType as IntegrationEventType,
  DataSourceType,
  DataPriority
} from './DataIntegrationService';
import { EventType } from '../types/event';

// Create a singleton instance of the DataIntegrationService
const dataIntegrationService = DataIntegrationService.getInstance();

// Map EventTypes if needed
type EventTypeMap = {
  [K in IntegrationEventType]: EventType;
};

const eventTypeMap: EventTypeMap = {
  [IntegrationEventType.DATA_UPDATE]: EventType.DATA_UPDATE,
  [IntegrationEventType.THRESHOLD_EXCEEDED]: EventType.THRESHOLD_EXCEEDED,
  [IntegrationEventType.ANOMALY_DETECTED]: EventType.ANOMALY_DETECTED,
  [IntegrationEventType.DEVICE_STATUS]: EventType.DEVICE_STATUS,
  [IntegrationEventType.SYSTEM_STATUS]: EventType.SYSTEM_STATUS
};

// 이벤트 처리 규칙 정의
export interface EventRule {
  id: string;
  name: string;
  description?: string;
  isActive: boolean;
  priority: number; // 높을수록 먼저 실행
  condition: {
    sourceTypes?: DataSourceType[];
    sourceIds?: string[];
    eventTypes?: EventType[];
    dataPatterns?: Array<{
      field: string;
      operator: 'eq' | 'neq' | 'gt' | 'gte' | 'lt' | 'lte' | 'contains' | 'startsWith' | 'endsWith' | 'regex';
      value: any;
    }>;
    customValidator?: (event: Event) => boolean;
  };
  actions: Array<{
    type: 'notify' | 'log' | 'escalate' | 'alert' | 'automate' | 'customAction';
    config?: any;
    handler?: (event: Event) => void;
  }>;
  executionCount: number;
  lastExecuted?: number;
}

// 이벤트 처리 결과 정의
export interface EventProcessingResult {
  eventId: string;
  timestamp: number;
  matchedRules: string[];
  actionsExecuted: Array<{
    ruleId: string;
    actionType: string;
    success: boolean;
    details?: any;
  }>;
}

// 알림 수신자 정의
export interface NotificationReceiver {
  id: string;
  name: string;
  type: 'email' | 'sms' | 'app' | 'webhook';
  target: string; // 이메일 주소, 전화번호, 웹훅 URL 등
  isActive: boolean;
}

/**
 * EventProcessorService
 * 데이터 통합 서비스에서 발생하는 이벤트를 처리하는 서비스
 * 이벤트 규칙을 등록하고 관리하며, 이벤트 발생 시 규칙에 따라 적절한 액션을 실행합니다.
 */
export class EventProcessorService {
  private rules: EventRule[] = [];
  private processingHistory: EventProcessingResult[] = [];
  private receivers: NotificationReceiver[] = [];
  private eventListenerUnsubscribe: (() => void) | null = null;
  
  // 싱글톤 인스턴스
  private static instance: EventProcessorService;
  
  constructor() {
    // 기본 규칙 로드
    this.loadDefaultRules();
    
    // 기본 수신자 로드
    this.loadDefaultReceivers();
    
    // 이벤트 구독 시작
    this.startEventProcessing();
  }
  
  public static getInstance(): EventProcessorService {
    if (!EventProcessorService.instance) {
      EventProcessorService.instance = new EventProcessorService();
    }
    return EventProcessorService.instance;
  }
  
  // 이벤트 처리 시작
  public startEventProcessing(): void {
    if (this.eventListenerUnsubscribe) {
      return; // 이미 처리 중이면 중복 시작 방지
    }
    
    // 모든 이벤트 구독
    this.eventListenerUnsubscribe = dataIntegrationService.addEventListenerForAll((event) => {
      this.processEvent(event);
    });
    
    console.log('이벤트 처리 시작됨');
  }
  
  // 이벤트 처리 중지
  public stopEventProcessing(): void {
    if (this.eventListenerUnsubscribe) {
      this.eventListenerUnsubscribe();
      this.eventListenerUnsubscribe = null;
      console.log('이벤트 처리 중지됨');
    }
  }
  
  // 규칙 관리
  public addRule(rule: Omit<EventRule, 'id' | 'executionCount'>): EventRule {
    const newRule: EventRule = {
      ...rule,
      id: `rule_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      executionCount: 0
    };
    
    this.rules.push(newRule);
    this.sortRulesByPriority();
    
    return newRule;
  }
  
  public updateRule(ruleId: string, updates: Partial<EventRule>): boolean {
    const ruleIndex = this.rules.findIndex(r => r.id === ruleId);
    if (ruleIndex >= 0) {
      this.rules[ruleIndex] = { ...this.rules[ruleIndex], ...updates };
      this.sortRulesByPriority();
      return true;
    }
    return false;
  }
  
  public deleteRule(ruleId: string): boolean {
    const initialLength = this.rules.length;
    this.rules = this.rules.filter(r => r.id !== ruleId);
    return initialLength !== this.rules.length;
  }
  
  public getRule(ruleId: string): EventRule | undefined {
    return this.rules.find(r => r.id === ruleId);
  }
  
  public getAllRules(): EventRule[] {
    return [...this.rules];
  }
  
  // 수신자 관리
  public addReceiver(receiver: Omit<NotificationReceiver, 'id'>): NotificationReceiver {
    const newReceiver: NotificationReceiver = {
      ...receiver,
      id: `receiver_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`
    };
    
    this.receivers.push(newReceiver);
    return newReceiver;
  }
  
  public updateReceiver(receiverId: string, updates: Partial<NotificationReceiver>): boolean {
    const receiverIndex = this.receivers.findIndex(r => r.id === receiverId);
    if (receiverIndex >= 0) {
      this.receivers[receiverIndex] = { ...this.receivers[receiverIndex], ...updates };
      return true;
    }
    return false;
  }
  
  public deleteReceiver(receiverId: string): boolean {
    const initialLength = this.receivers.length;
    this.receivers = this.receivers.filter(r => r.id !== receiverId);
    return initialLength !== this.receivers.length;
  }
  
  public getReceiver(receiverId: string): NotificationReceiver | undefined {
    return this.receivers.find(r => r.id === receiverId);
  }
  
  public getAllReceivers(): NotificationReceiver[] {
    return [...this.receivers];
  }
  
  // 이벤트 처리 및 기록 관리
  public getProcessingHistory(limit: number = 100): EventProcessingResult[] {
    return this.processingHistory.slice(0, limit);
  }
  
  public clearProcessingHistory(): void {
    this.processingHistory = [];
  }
  
  // 이벤트 수동 처리
  public manualProcessEvent(event: Event): EventProcessingResult {
    return this.processEvent(event);
  }
  
  // 이벤트 처리 핵심 로직
  private processEvent(event: Event): EventProcessingResult {
    const processingResult: EventProcessingResult = {
      eventId: event.id,
      timestamp: Date.now(),
      matchedRules: [],
      actionsExecuted: []
    };
    
    // 활성화된 규칙만 처리
    const activeRules = this.rules.filter(rule => rule.isActive);
    
    for (const rule of activeRules) {
      // 규칙 조건 확인
      if (!this.checkRuleCondition(event, rule.condition)) {
        continue;
      }
      
      // 규칙이 일치하면 결과에 추가
      processingResult.matchedRules.push(rule.id);
      
      // 규칙의 모든 액션 실행
      for (const action of rule.actions) {
        try {
          const actionResult = this.executeAction(event, action);
          
          processingResult.actionsExecuted.push({
            ruleId: rule.id,
            actionType: action.type,
            success: true,
            details: actionResult
          });
        } catch (error) {
          console.error(`액션 실행 오류 (규칙 ${rule.id}, 이벤트 ${event.id}):`, error);
          
          processingResult.actionsExecuted.push({
            ruleId: rule.id,
            actionType: action.type,
            success: false,
            details: { error: error instanceof Error ? error.message : String(error) }
          });
        }
      }
      
      // 규칙 실행 카운터 증가 및 마지막 실행 시간 업데이트
      rule.executionCount++;
      rule.lastExecuted = Date.now();
    }
    
    // 처리 결과 기록
    this.processingHistory.unshift(processingResult);
    if (this.processingHistory.length > 1000) {
      this.processingHistory = this.processingHistory.slice(0, 1000);
    }
    
    return processingResult;
  }
  
  // 규칙 조건 확인
  private checkRuleCondition(event: Event, condition: EventRule['condition']): boolean {
    // 소스 타입 확인
    if (condition.sourceTypes && condition.sourceTypes.length > 0) {
      if (!condition.sourceTypes.includes(event.sourceType)) {
        return false;
      }
    }
    
    // 소스 ID 확인
    if (condition.sourceIds && condition.sourceIds.length > 0) {
      if (!condition.sourceIds.includes(event.sourceId)) {
        return false;
      }
    }
    
    // 이벤트 타입 확인
    if (condition.eventTypes && condition.eventTypes.length > 0) {
      if (!condition.eventTypes.includes(event.eventType)) {
        return false;
      }
    }
    
    // 데이터 패턴 확인
    if (condition.dataPatterns && condition.dataPatterns.length > 0) {
      for (const pattern of condition.dataPatterns) {
        // 필드 경로 처리 (예: 'data.value', 'data.status')
        const fieldParts = pattern.field.split('.');
        let fieldValue: unknown = event;
        
        for (const part of fieldParts) {
          if (fieldValue === undefined || fieldValue === null) {
            return false;
          }
          if (typeof fieldValue !== 'object') {
            return false;
          }
          fieldValue = (fieldValue as Record<string, unknown>)[part];
        }
        
        // 연산자에 따른 비교
        switch (pattern.operator) {
          case 'eq':
            if (fieldValue !== pattern.value) return false;
            break;
          case 'neq':
            if (fieldValue === pattern.value) return false;
            break;
          case 'gt':
            if (typeof fieldValue !== 'number' || fieldValue <= pattern.value) return false;
            break;
          case 'gte':
            if (typeof fieldValue !== 'number' || fieldValue < pattern.value) return false;
            break;
          case 'lt':
            if (typeof fieldValue !== 'number' || fieldValue >= pattern.value) return false;
            break;
          case 'lte':
            if (typeof fieldValue !== 'number' || fieldValue > pattern.value) return false;
            break;
          case 'contains':
            if (typeof fieldValue !== 'string' || !fieldValue.includes(pattern.value)) return false;
            break;
          case 'startsWith':
            if (typeof fieldValue !== 'string' || !fieldValue.startsWith(pattern.value)) return false;
            break;
          case 'endsWith':
            if (typeof fieldValue !== 'string' || !fieldValue.endsWith(pattern.value)) return false;
            break;
          case 'regex':
            if (typeof fieldValue !== 'string' || !new RegExp(pattern.value).test(fieldValue)) return false;
            break;
          default:
            console.warn(`알 수 없는 연산자: ${pattern.operator}`);
            return false;
        }
      }
    }
    
    // 커스텀 검증기 확인
    if (condition.customValidator && !condition.customValidator(event)) {
      return false;
    }
    
    return true;
  }
  
  // 액션 실행
  private executeAction(event: Event, action: EventRule['actions'][0]): any {
    switch (action.type) {
      case 'notify':
        return this.executeNotifyAction(event, action.config);
      
      case 'log':
        return this.executeLogAction(event, action.config);
      
      case 'escalate':
        return this.executeEscalateAction(event, action.config);
      
      case 'alert':
        return this.executeAlertAction(event, action.config);
      
      case 'automate':
        return this.executeAutomateAction(event, action.config);
      
      case 'customAction':
        if (action.handler) {
          return action.handler(event);
        }
        throw new Error('커스텀 액션 핸들러가 제공되지 않음');
      
      default:
        throw new Error(`지원되지 않는 액션 타입: ${action.type}`);
    }
  }
  
  // 알림 액션 실행
  private executeNotifyAction(event: Event, config: any): any {
    const { receiverIds, message, includeEventDetails = true } = config || {};
    
    if (!receiverIds || !Array.isArray(receiverIds) || receiverIds.length === 0) {
      throw new Error('수신자 ID가 지정되지 않음');
    }
    
    const targetReceivers = this.receivers.filter(
      r => r.isActive && receiverIds.includes(r.id)
    );
    
    if (targetReceivers.length === 0) {
      console.warn('활성화된 수신자를 찾을 수 없습니다.');
      return { sentCount: 0, message: '수신자를 찾을 수 없음' };
    }
    
    // 실제 알림 전송 (이 예제에서는 콘솔 로깅으로 대체)
    console.log(`[알림 전송] 이벤트: ${event.id}`);
    console.log(`수신자: ${targetReceivers.map(r => r.name).join(', ')}`);
    console.log(`메시지: ${message || '이벤트 알림'}`);
    
    if (includeEventDetails) {
      console.log('이벤트 세부정보:', JSON.stringify(event, null, 2));
    }
    
    return {
      sentCount: targetReceivers.length,
      receivers: targetReceivers.map(r => r.id)
    };
  }
  
  // 로깅 액션 실행
  private executeLogAction(event: Event, config: any): any {
    const { level = 'info', message } = config || {};
    
    const logEntry = {
      timestamp: new Date().toISOString(),
      level,
      eventId: event.id,
      sourceId: event.sourceId,
      sourceType: event.sourceType,
      eventType: event.eventType,
      message: message || `이벤트 로깅: ${event.eventType}`,
      data: event.data
    };
    
    // 로그 레벨에 따라 다른 콘솔 메서드 사용
    switch (level) {
      case 'error':
        console.error('[EventProcessor]', logEntry);
        break;
      case 'warn':
        console.warn('[EventProcessor]', logEntry);
        break;
      case 'debug':
        console.debug('[EventProcessor]', logEntry);
        break;
      case 'info':
      default:
        console.log('[EventProcessor]', logEntry);
        break;
    }
    
    return { logged: true, level };
  }
  
  // 에스컬레이션 액션 실행
  private executeEscalateAction(event: Event, config: any): any {
    const { priority = DataPriority.HIGH, message, targetReceiverIds } = config || {};
    
    // 우선순위 높은 새 이벤트 생성
    const escalatedEvent: Event = {
      id: `escalated_${event.id}`,
      timestamp: Date.now(),
      sourceType: event.sourceType,
      sourceId: event.sourceId,
      eventType: event.eventType,
      priority, // 지정된 우선순위로 설정
      data: {
        originalEvent: event,
        escalationReason: message || '자동 에스컬레이션',
        escalatedAt: new Date().toISOString()
      }
    };
    
    // 새 이벤트 추가
    console.log('Processing escalated event:', escalatedEvent);
    
    // 지정된 수신자에게 알림 추가 전송 (선택적)
    if (targetReceiverIds && Array.isArray(targetReceiverIds) && targetReceiverIds.length > 0) {
      try {
        this.executeNotifyAction(escalatedEvent, {
          receiverIds: targetReceiverIds,
          message: `[에스컬레이션] ${message || '이벤트 에스컬레이션'}`,
          includeEventDetails: true
        });
      } catch (error) {
        console.error('에스컬레이션 알림 전송 오류:', error);
      }
    }
    
    return {
      escalated: true,
      newEventId: escalatedEvent.id,
      priority
    };
  }
  
  // 알림 액션 실행 (UI에 표시될 알림)
  private executeAlertAction(event: Event, config: any): any {
    const { title, message, type = 'info', autoClose = true, duration = 5000 } = config || {};
    
    // 알림 생성 (실제로는 UI 라이브러리/시스템과 연동 필요)
    const alertData = {
      id: `alert_${Date.now()}`,
      title: title || `${event.sourceType} 알림`,
      message: message || `${event.eventType} 이벤트가 발생`,
      type, // 'info', 'warning', 'error', 'success' 등
      autoClose,
      duration,
      createdAt: new Date().toISOString(),
      eventId: event.id
    };
    
    // 개발 환경에서는 콘솔로 알림 표시
    console.log(`[UI 알림] ${alertData.title}: ${alertData.message}`);
    
    // 실제 구현에서는 알림 관리 시스템으로 전달
    // alertManager.showAlert(alertData);
    
    return {
      alertId: alertData.id,
      shown: true
    };
  }
  
  // 자동화 액션 실행
  private executeAutomateAction(event: Event, config: any): any {
    const { actionType, targetId, parameters = {} } = config || {};
    
    if (!actionType) {
      throw new Error('자동화 액션 타입이 지정되지 않음');
    }
    
    // 자동화 작업 로깅
    console.log(`[자동화 액션] 타입: ${actionType}, 대상: ${targetId || '없음'}`);
    console.log('매개변수:', parameters);
    console.log('트리거 이벤트:', event.id);
    
    // 여기서는 실제 자동화를 수행하지 않고 성공했다고 가정
    // 실제 구현에서는 해당 자동화 시스템과 연동 필요
    
    return {
      executed: true,
      actionType,
      targetId,
      timestamp: new Date().toISOString()
    };
  }
  
  // 규칙을 우선순위에 따라 정렬
  private sortRulesByPriority(): void {
    this.rules.sort((a, b) => b.priority - a.priority);
  }
  
  // 기본 규칙 로드
  private loadDefaultRules(): void {
    // 규칙 1: 심각한 임계값 초과 알림
    this.addRule({
      name: '심각한 임계값 초과 경고',
      description: '중요 시스템 지표의 임계값을 초과하면 관리자에게 알림',
      isActive: true,
      priority: 100,
      condition: {
        eventTypes: [EventType.THRESHOLD_EXCEEDED],
        dataPatterns: [
          { field: 'data.severity', operator: 'eq', value: DataPriority.CRITICAL }
        ]
      },
      actions: [
        {
          type: 'notify',
          config: {
            receiverIds: ['admin_email', 'admin_sms'],
            message: '심각: 시스템 임계값 초과 감지',
            includeEventDetails: true
          }
        },
        {
          type: 'alert',
          config: {
            title: '심각한 경고',
            message: '중요 시스템 지표가 임계값을 초과함',
            type: 'error',
            autoClose: false
          }
        }
      ]
    });
    
    // 규칙 2: 염소 농도 이상 감지
    this.addRule({
      name: '염소 농도 이상 감지',
      description: '시설 염소 농도가 안전 범위를 벗어나면 즉시 알림',
      isActive: true,
      priority: 90,
      condition: {
        sourceTypes: [DataSourceType.SENSOR],
        eventTypes: [EventType.ANOMALY_DETECTED],
        dataPatterns: [
          { field: 'sourceId', operator: 'contains', value: 'chlorine' }
        ]
      },
      actions: [
        {
          type: 'notify',
          config: {
            receiverIds: ['pool_manager', 'maintenance_staff'],
            message: '주의: 시설 염소 농도 이상 감지',
            includeEventDetails: true
          }
        },
        {
          type: 'alert',
          config: {
            title: '수질 경고',
            message: '염소 농도가 안전 범위를 벗어남. 확인 필요',
            type: 'warning'
          }
        }
      ]
    });
    
    // 규칙 3: 장비 오프라인 감지
    this.addRule({
      name: '장비 오프라인 알림',
      description: '중요 센서나 카메라가 오프라인 상태로 변경되면 알림',
      isActive: true,
      priority: 80,
      condition: {
        eventTypes: [EventType.DEVICE_STATUS],
        dataPatterns: [
          { field: 'data.status', operator: 'eq', value: 'offline' }
        ]
      },
      actions: [
        {
          type: 'log',
          config: {
            level: 'warn',
            message: '장비 오프라인: {sourceId}'
          }
        },
        {
          type: 'notify',
          config: {
            receiverIds: ['maintenance_staff'],
            message: '장비 오프라인 감지',
            includeEventDetails: true
          }
        }
      ]
    });
    
    // 규칙 4: 온도 변화 로깅
    this.addRule({
      name: '온도 데이터 로깅',
      description: '모든 온도 센서 데이터 업데이트 로깅',
      isActive: true,
      priority: 10,
      condition: {
        sourceTypes: [DataSourceType.SENSOR],
        eventTypes: [EventType.DATA_UPDATE],
        dataPatterns: [
          { field: 'sourceId', operator: 'contains', value: 'temp' }
        ]
      },
      actions: [
        {
          type: 'log',
          config: {
            level: 'info',
            message: '온도 업데이트: {data.value}{data.unit}'
          }
        }
      ]
    });
  }
  
  // 기본 수신자 로드
  private loadDefaultReceivers(): void {
    // 관리자 이메일
    this.addReceiver({
      name: '관리자 이메일',
      type: 'email',
      target: 'admin@example.com',
      isActive: true
    });
    
    // 관리자 SMS
    this.addReceiver({
      name: '관리자 SMS',
      type: 'sms',
      target: '+82100000000',
      isActive: true
    });
    
    // 시설 관리자
    this.addReceiver({
      name: '시설 관리자',
      type: 'email',
      target: 'pool_manager@example.com',
      isActive: true
    });
    
    // 유지보수 담당자
    this.addReceiver({
      name: '유지보수 담당자',
      type: 'app',
      target: 'maintenance_team',
      isActive: true
    });
  }
}

// 싱글톤 인스턴스 생성 및 내보내기
const eventProcessorService = EventProcessorService.getInstance();
export default eventProcessorService; 